/**
 * Sanitization de fragments SVG (contenu interne d'un <svg>, sans la racine).
 *
 * Pourquoi un helper dédié :
 *   DOMPurify est en mode HTML par défaut. Quand on lui passe un fragment
 *   genre '<path d="..."/><path d="..."/>' (sans racine <svg>), il considère
 *   les balises <path>/<g>/<polygon>/etc. comme des éléments HTML inconnus
 *   et les strippe — même avec USE_PROFILES.svg activé. Résultat : tous nos
 *   pictogrammes officiels (GHS, vape, etc.) apparaissaient vides à l'écran.
 *
 * La technique standard :
 *   1. On wrappe le fragment dans un <svg> root
 *   2. On sanitize avec le profil svg + svgFilters
 *   3. On extrait le contenu intérieur du <svg> nettoyé
 *
 * Ce qui reste retiré : <script>, attributs on*, iframes, javascript:, etc.
 * Ce qui est conservé : tags SVG légitimes (path, g, polygon, defs,
 * linearGradient, radialGradient, stop, filter, use, mask, clipPath…) et
 * leurs attributs (d, fill, stroke, style, transform, etc.).
 */
import DOMPurify from 'dompurify';


// Match le wrapper <svg ...> ... </svg> ajouté ci-dessous pour extraire le
// contenu interne après nettoyage.
const WRAPPER_RE = /^<svg[^>]*>([\s\S]*)<\/svg>\s*$/i;


/**
 * Sanitize un fragment SVG (sans la balise <svg> racine).
 *
 * @param {string} fragment - contenu SVG interne (ex: '<path d="..."/><g>...</g>')
 * @returns {string} fragment nettoyé, prêt à être injecté via dangerouslySetInnerHTML
 *                   à l'intérieur d'un <svg viewBox="..."> existant.
 */
export function sanitizeSvgFragment(fragment) {
  if (!fragment || typeof fragment !== 'string') return '';

  // 1. Wrap pour donner un contexte SVG au parser DOMPurify.
  //    xmlns explicite parce que certains parsers HTML refusent les balises
  //    SVG sans namespace.
  const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${fragment}</svg>`;

  // 2. Sanitize avec le profil SVG complet.
  const clean = DOMPurify.sanitize(wrapped, {
    USE_PROFILES: { svg: true, svgFilters: true },
    // Conserve les attributs style="fill:#...;stroke:..." qui sont
    // omniprésents dans nos pictos officiels CLP.
    ALLOW_DATA_ATTR: false,
  });

  // 3. Unwrap — récupère uniquement le contenu interne du <svg>.
  const match = String(clean).match(WRAPPER_RE);
  return match ? match[1] : '';
}
